import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ExportOutlined } from '@ant-design/icons';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { isInternalTech, isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import OWButton from '../ow_button/OWButton';
import RequestToAddSupplierForm from '../request_to_add_supplier_form/RequestToAddSupplierForm';
import posthog from 'posthog-js';
import { refreshEntityAddRequestCounts } from '../../thunks/entity_add_requests_thunks';
import {
	ENTITY_REQUEST_ENTITY_TYPES,
	ENTITY_ADD_REQUEST_STATUSES,
	REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG,
} from '../../types/entity_add_requests';

const LINK_COLOR = '#6027b0';
const COUNT_COLOR = '#faad14';

const getRequestsListUrl = (userType) => `/${userType}/requestsToAdd/overview/suppliers`;

// The Requests-to-Add flow is the alternative to adding a supplier directly. It is shown only when the
// user can request new suppliers (requestNewSuppliers) but CANNOT add them (MODIFY_SUPPLIERS) — "Add
// Supplier" and "Request to Add" are mutually exclusive. Available to buyers and to internal service
// teams (this page is shared by both); third-party suppliers never see it. When neither the indicator
// nor the request button should appear, both components below render null.
const getShowRequestFlow = (state) => {
	const userType = nullSafeGetOrElse('session.userType', state, '');
	const currentUser = nullSafeGetOrElse('session.currentUser', state, {});
	const companyConfig = nullSafeGetOrElse('company_config.detail', state, {});
	const roles = nullSafeGetOrElse('session.currentUser.roles', state, []);
	const isAllowedRole = userType === ROLE_TYPES.BUYER || isInternalTech(currentUser);
	const canRequest = isUserAllowedToAccess(userType)(
		PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS,
		companyConfig,
		roles
	);
	const canAddSupplier = isUserAllowedToAccess(userType)(
		PERMISSION_NAMES.MODIFY_SUPPLIERS,
		companyConfig,
		roles
	);
	const featureEnabled = !!posthog.isFeatureEnabled(REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG);
	return featureEnabled && isAllowedRole && canRequest && !canAddSupplier;
};

// `userType` reaches ownProps via the withUserType wrapper around each connected export below.
const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshCounts: () =>
		dispatch(
			refreshEntityAddRequestCounts(ownProps.userType)({
				entityType: ENTITY_REQUEST_ENTITY_TYPES.supplier,
			})
		),
});

// Clickable "N Pending Approvals" / "All Requests" indicator. Sits to the LEFT of the Export menu and
// opens the full Requests-to-Add list in a new browser tab (prefiltered to pending when any exist).
function PendingApprovalsIndicatorBase({ showRequestFlow, pendingCount, userType, refreshCounts }) {
	// showRequestFlow depends on company_config.detail, which may load after this mounts; refresh the
	// counts whenever the flow becomes visible so pendingCount isn't stuck at its initial 0.
	useEffect(() => {
		if (showRequestFlow) {
			refreshCounts();
		}
	}, [showRequestFlow, refreshCounts]);

	const openRequestsList = () => {
		const requestsListUrl = getRequestsListUrl(userType);
		const url =
			pendingCount > 0
				? `${requestsListUrl}?statuses=${ENTITY_ADD_REQUEST_STATUSES.pending_review}`
				: requestsListUrl;
		window.open(url, '_blank');
	};

	if (!showRequestFlow) {
		return null;
	}

	const hasPending = pendingCount > 0;

	return (
		<span
			className="bodymedium mr-4 flex cursor-pointer flex-row items-center"
			style={{ gap: '6px', color: LINK_COLOR }}
			onClick={openRequestsList}
		>
			{hasPending ? (
				<span>
					<span className="bodybold" style={{ color: COUNT_COLOR }}>
						{pendingCount}
					</span>{' '}
					Pending Approvals
				</span>
			) : (
				<span>All Requests</span>
			)}
			<ExportOutlined />
		</span>
	);
}

export const SuppliersPendingApprovalsIndicator = withUserType(
	connect(
		(state) => ({
			showRequestFlow: getShowRequestFlow(state),
			pendingCount: nullSafeGetOrElse('counts.pending_review', state.entity_add_requests, 0),
		}),
		mapDispatchToProps
	)(PendingApprovalsIndicatorBase)
);

// "REQUEST TO ADD" button, rendered in the same position as "Add Supplier" (only one is ever shown).
// Opens the new-supplier request form in place.
function RequestToAddButtonBase({ showRequestFlow, refreshCounts }) {
	const [formVisible, setFormVisible] = useState(false);

	const openForm = () => setFormVisible(true);
	const hideForm = () => setFormVisible(false);
	const handleFormSuccess = () => {
		setFormVisible(false);
		refreshCounts();
	};

	if (!showRequestFlow) {
		return null;
	}

	return (
		<>
			<OWButton size="large" type="primary" onClick={openForm} className="inline-block-visible-md">
				REQUEST TO ADD
			</OWButton>
			{formVisible ? (
				<RequestToAddSupplierForm
					visible={formVisible}
					record={undefined}
					hideModal={hideForm}
					onSuccess={handleFormSuccess}
				/>
			) : null}
		</>
	);
}

export const SuppliersRequestToAddButton = withUserType(
	connect(
		(state) => ({ showRequestFlow: getShowRequestFlow(state) }),
		mapDispatchToProps
	)(RequestToAddButtonBase)
);
